// src/components/Login.js - Updated with registration and password reset toggle
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useLocation, useNavigate } from 'react-router-dom';
import PasswordReset from './PasswordReset';
import EmailVerificationForm from './EmailVerificationForm';
import Button from './Button';
import Icon from './Icon';
import { trackEvent } from '../utils/analytics';
import { GoogleLogin } from '@react-oauth/google';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8000';

function Login({ onLogin }) {
  const location = useLocation();
  const navigate = useNavigate();
  const [showPasswordReset, setShowPasswordReset] = useState(false);
  const [showEmailVerification, setShowEmailVerification] = useState(false);
  const [verificationEmail, setVerificationEmail] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  // Get message from location state (for invite redirects, password reset success, etc.)
  const locationMessage = location.state?.message;

  // Check for registration success data on component mount
  useEffect(() => {
    const registrationSuccess = sessionStorage.getItem('registrationSuccess');
    if (registrationSuccess) {
      try {
        const data = JSON.parse(registrationSuccess);
        setEmail(data.email || '');
        if (data.message) {
          setSuccessMessage(data.message);
        }
        // Store saved messages for potential dashboard restoration
        if (data.savedMessages) {
          sessionStorage.setItem('instantChatMessages', JSON.stringify(data.savedMessages));
        }
        // Clear the registration success data
        sessionStorage.removeItem('registrationSuccess');
      } catch (e) {
        console.error('Error parsing registration success data:', e);
        sessionStorage.removeItem('registrationSuccess');
      }
    }

    // Also check location state for messages
    if (location.state?.email) {
      setEmail(location.state.email);
    }
    if (location.state?.message) {
      setSuccessMessage(location.state.message);
    }
  }, [location.state]);

  const handleGoogleLogin = async (credentialResponse) => {
    setLoading(true);
    setError('');

    try {
      console.log('🔄 Attempting Google login...');

      const response = await axios.post(`${API_BASE}/auth/google/login`, {
        credential: credentialResponse.credential
      });

      const token = response.data.access_token;
      const userData = response.data.user;
      const isNewUser = response.data.is_new_user;

      console.log('✅ Google login successful for:', userData.email);
      console.log('🆕 Is new user:', isNewUser);

      // Track Google login/signup
      trackEvent(isNewUser ? 'User Registered' : 'User Logged In', {
        method: 'google',
        role: userData.role
      });

      // If this is a new user on the login page, redirect to express signup for account setup
      if (isNewUser) {
        console.log('🆕 New Google user detected on login page - redirecting to express signup');
        // Store token temporarily
        localStorage.setItem('therapy_auth_token', token);
        // Redirect to express signup which will show account setup
        navigate('/express-signup', { 
          state: { 
            googleAuth: true,
            email: userData.email,
            token: token,
            userData: userData
          } 
        });
        return;
      }

      // Pass returnTo from location state if it exists
      const returnTo = location.state?.returnTo;
      onLogin(token, userData, returnTo);
    } catch (err) {
      console.error('❌ Google login error:', err);
      setError(err.response?.data?.detail || 'Google-inloggning misslyckades');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      console.log('🔄 Attempting login for:', email);

      const response = await axios.post(`${API_BASE}/auth/token`, 
        `username=${encodeURIComponent(email)}&password=${encodeURIComponent(password)}`,
        {
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
          }
        }
      );

      const token = response.data.access_token;
      
      // Get user info to determine role and setup
      const userResponse = await axios.get(`${API_BASE}/auth/me`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      const userData = userResponse.data;
      console.log('✅ Login successful for:', userData.role, userData.email);
      
            // Convert anonymous messages if available and not already converted
      const savedMessages = sessionStorage.getItem('instantChatMessages');
      if (savedMessages && !sessionStorage.getItem('messagesAlreadyConverted')) {
        try {
          const messages = JSON.parse(savedMessages);
          sessionStorage.removeItem('instantChatMessages');
          
          if (messages.length > 0) {
            console.log(`🔄 Converting ${messages.length} anonymous messages to ultra-secure chat...`);
            
            // Call the conversion endpoint
            const convertResponse = await axios.post(
              `${API_BASE}/auth/convert-anonymous-messages`,
              { messages },
              { headers: { 'Authorization': `Bearer ${token}` } }
            );
            
            if (convertResponse.data.success) {
              console.log(`✅ Successfully converted ${convertResponse.data.converted_count} messages to ultra-secure chat`);
              setSuccessMessage(`Dina tidigare meddelanden har sparats säkert i ditt konto (${convertResponse.data.converted_count} konversationer)`);
              sessionStorage.setItem('messagesAlreadyConverted', 'true');
            }
          }
        } catch (conversionError) {
          console.error('⚠️ Error converting anonymous messages:', conversionError);
          // Don't fail login if conversion fails
          sessionStorage.removeItem('instantChatMessages');
        }
      } else if (sessionStorage.getItem('messagesAlreadyConverted')) {
        console.log('⏭️ Messages already converted during express signup, skipping...');
      }
      
      // 🆕 INITIALIZE ANAMNESIS PROFILE FOR NEW PATIENTS
      if (userData.role === 'patient') {
        try {
          console.log('🌱 Checking/initializing anamnesis profile for patient...');
          
          // First check if profile already exists
          const profileCheckResponse = await axios.get(`${API_BASE}/api/progressive-anamnesis/progress`, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          
          console.log('✅ Existing anamnesis profile found');
        } catch (profileError) {
          if (profileError.response?.status === 404) {
            // Profile doesn't exist, create it
            console.log('📝 No anamnesis profile found, creating new one...');
            try {
              const anamnesisResponse = await axios.post(`${API_BASE}/api/progressive-anamnesis/initialize-profile`, {}, {
                headers: { 'Authorization': `Bearer ${token}` }
              });
              
              console.log('✅ Anamnesis profile created for new patient:', anamnesisResponse.data);
            } catch (createError) {
              console.warn('⚠️ Failed to create anamnesis profile (non-critical):', createError);
            }
          } else {
            console.warn('⚠️ Error checking anamnesis profile (non-critical):', profileError);
          }
        }
      }
      
      // Pass returnTo from location state if it exists
      const returnTo = location.state?.returnTo;
      onLogin(token, userData, returnTo);
    } catch (err) {
      console.error('❌ Login error:', err);
      
      // Check if it's an email verification error (403 status with verification message)
      const isVerificationError = err.response?.status === 403 && 
        (err.response?.headers?.['x-verification-required'] || 
         err.response?.headers?.['X-Verification-Required'] ||
         (err.response?.data?.detail && err.response.data.detail.includes('6-siffrig verifieringskod')));
      
      if (isVerificationError) {
        console.log('🔄 Detected verification error, showing EmailVerificationForm');
        // Show inline verification instead of old message
        setShowEmailVerification(true);
        setVerificationEmail(email);
        setError('');
      } else {
        setError(err.response?.data?.detail || 'Inloggning misslyckades');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleRegistrationSuccess = ({ message, user, autoLogin, token, returnTo: regReturnTo }) => {
    setSuccessMessage(message);
    
    if (autoLogin && token && regReturnTo) {
      // Auto-login case from invitation
      console.log('🔄 Handling auto-login after registration...');
      onLogin(token, user, regReturnTo);
    } else {
      // Normal registration case
      setEmail(user.email); // Pre-fill email for login
      
      // Clear success message after 5 seconds
      setTimeout(() => setSuccessMessage(''), 5000);
    }
  };

  const handleVerificationSuccess = async (verifiedEmail) => {
    console.log('✅ Email verification successful, attempting login...');
    // Don't hide verification form until we're ready to navigate
    
    try {
      // Log in the user after successful email verification
      console.log('🔄 Attempting login after email verification for:', verifiedEmail);

      const response = await axios.post(`${API_BASE}/auth/token`, 
        `username=${encodeURIComponent(verifiedEmail)}&password=${encodeURIComponent(password)}`,
        {
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
          }
        }
      );

      const token = response.data.access_token;
      
      // Get user info to determine role and setup
      const userResponse = await axios.get(`${API_BASE}/auth/me`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      const userData = userResponse.data;
      console.log('✅ Login successful after verification for:', userData.role, userData.email);
      
      // Check if there are saved instant chat messages to convert (for express signup users)
      const savedMessages = sessionStorage.getItem('instantChatMessages');
      let parsedMessages = [];
      
      if (savedMessages && userData.role === 'patient') {
        try {
          parsedMessages = JSON.parse(savedMessages);
          
          if (parsedMessages.length > 0) {
            console.log(`🔄 Converting ${parsedMessages.length} anonymous messages to ultra-secure chat...`);
            
            // Call the conversion endpoint
            const convertResponse = await axios.post(
              `${API_BASE}/auth/convert-anonymous-messages`,
              { messages: parsedMessages },
              { headers: { 'Authorization': `Bearer ${token}` } }
            );
            
            if (convertResponse.data.success) {
              console.log(`✅ Successfully converted ${convertResponse.data.converted_count} messages to ultra-secure chat`);
              setSuccessMessage(`Dina tidigare meddelanden har sparats säkert i ditt konto (${convertResponse.data.converted_count} konversationer)`);
            }
          }
        } catch (conversionError) {
          console.error('⚠️ Error converting anonymous messages:', conversionError);
          // Don't fail login if conversion fails
        }
      }
      
      // Initialize anamnesis profile for new patients
      if (userData.role === 'patient') {
        try {
          console.log('🌱 Checking/initializing anamnesis profile for patient...');
          
          // First check if profile already exists
          const profileCheckResponse = await axios.get(`${API_BASE}/api/progressive-anamnesis/progress`, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          
          console.log('✅ Existing anamnesis profile found');
        } catch (profileError) {
          if (profileError.response?.status === 404) {
            // Profile doesn't exist, create it
            console.log('📝 No anamnesis profile found, creating new one...');
            try {
              // Create initialization payload with anonymous messages for analysis if they exist
              const initPayload = {};
              if (parsedMessages.length > 0) {
                initPayload.anonymous_messages = parsedMessages;
                console.log(`📋 Including ${parsedMessages.length} anonymous messages for anamnesis analysis`);
              }
              
              const anamnesisResponse = await axios.post(`${API_BASE}/api/progressive-anamnesis/initialize-profile`, initPayload, {
                headers: { 'Authorization': `Bearer ${token}` }
              });
              
              console.log('✅ Anamnesis profile created for new patient with message analysis:', anamnesisResponse.data);
            } catch (createError) {
              console.warn('⚠️ Failed to create anamnesis profile (non-critical):', createError);
            }
          } else {
            console.warn('⚠️ Error checking anamnesis profile (non-critical):', profileError);
          }
        }
      }
      
      // Pass returnTo from location state if it exists
      const returnTo = location.state?.returnTo;
      
      // Clean up session storage after successful processing
      if (parsedMessages.length > 0) {
        sessionStorage.removeItem('instantChatMessages');
        console.log('🧹 Cleaned up session storage after successful processing');
      }
      
      // Hide verification form only when we're about to navigate
      setShowEmailVerification(false);
      onLogin(token, userData, returnTo);
    } catch (error) {
      console.error('❌ Error logging in after verification:', error);
      setError('Ett fel uppstod vid inloggning efter verifiering. Försök logga in igen.');
      setShowEmailVerification(false);
    }
  };

  if (showPasswordReset) {
    return (
      <PasswordReset 
        onBackToLogin={() => setShowPasswordReset(false)}
      />
    );
  }

  if (showEmailVerification) {
    return (
      <EmailVerificationForm
        email={verificationEmail}
        onVerificationSuccess={handleVerificationSuccess}
        onBackToLogin={() => {
          setShowEmailVerification(false);
          setError('');
        }}
      />
    );
  }

  return (
    <div className="max-w-lg mx-auto my-8 p-8 border border-light rounded-lg bg-surface-primary shadow-custom-lg font-sans">
      <h2 className="text-2xl font-bold text-center mb-6 text-primary font-inter">Logga in</h2>
      
      {locationMessage && (
        <div className="bg-brand-primary/5 border border-brand-primary/20 rounded-lg p-4 mb-6">
          <div className="flex items-start space-x-3">
            <svg className="w-5 h-5 text-brand-primary mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <div>
              <h3 className="text-sm font-semibold text-brand-primary mb-1">🎯 Terapeutinbjudan</h3>
              <p className="text-sm text-brand-primary/80">{locationMessage}</p>
            </div>
          </div>
        </div>
      )}
      
      {successMessage && (
        <div className="bg-success-50 text-success-700 p-4 rounded mb-5 border border-success-500/20">
          ✅ {successMessage}
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="space-y-1">
          <input
            type="email"
            placeholder="E-post"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            className="w-full px-3 py-2 border border-light rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-brand-primary focus:border-transparent"
          />
        </div>
        <div className="space-y-1">
          <input
            type="password"
            placeholder="Lösenord"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            className="w-full px-3 py-2 border border-light rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-brand-primary focus:border-transparent"
          />
        </div>
        
        {error && (
          <div className="bg-error-50 text-error-700 p-3 rounded border border-error-500/20 text-sm">
            {error}
          </div>
        )}
        
        <Button type="submit" disabled={loading} variant="primary" className="w-full">
          {loading ? 'Loggar in...' : 'Logga in'}
        </Button>

        {/* Divider */}
        <div className="relative my-6">
          <div className="absolute inset-0 flex items-center">
            <div className="w-full border-t border-gray-300"></div>
          </div>
          <div className="relative flex justify-center text-sm">
            <span className="px-2 bg-white text-gray-500">Eller</span>
          </div>
        </div>

        {/* Google Sign-In Button */}
        <div className="flex justify-center">
          <GoogleLogin
            onSuccess={handleGoogleLogin}
            onError={() => {
              console.error('❌ Google login failed');
              setError('Google-inloggning misslyckades');
            }}
            text="signin_with"
            shape="rectangular"
            theme="outline"
            size="large"
          />
        </div>
      </form>

      {/* Password Reset Link */}
      <div className="text-center mt-4">
        <button 
          onClick={() => setShowPasswordReset(true)}
          className="text-sm text-brand-primary hover:text-brand-primary-dark underline transition-custom"
        >
          Glömt lösenordet?
        </button>
      </div>

      <div className="text-center mt-5 pt-5 border-t border-light">
        <p className="text-secondary mb-3">Har du inget konto?</p>
        
        {/* Different registration paths */}
        <div className="space-y-3">
          <div>
            <p className="text-sm text-secondary mb-2">Som terapeut:</p>
            <button 
              onClick={() => navigate('/register')}
              className="bg-white text-brand-primary border border-brand-primary px-4 py-2 rounded hover:bg-brand-secondary/90 transition-custom text-sm"
            >
              Skapa terapeutkonto
            </button>
          </div>
          
          <div className="pt-2">
            <p className="text-sm text-secondary mb-2">Som klient/patient:</p>
            <div className="flex gap-2 justify-center">
              <button 
                onClick={() => {
                  // Track the click event
                  trackEvent('Start Chat Button Click', { location: 'login-page' });
                  navigate('/instant-chat');
                }}
                className="bg-brand-primary text-white px-3 py-2 rounded hover:bg-brand-primary-dark transition-custom text-xs"
              >
                Börja direktchat
              </button>
              <button 
                onClick={() => {
                  // Track the click event
                  trackEvent('Express Signup Button Click', { location: 'login-page' });
                  navigate('/express-signup');
                }}
                className="bg-neutral-600 text-white px-3 py-2 rounded hover:bg-neutral-700 transition-custom text-xs"
              >
                Snabbregistrering
              </button>
            </div>
          </div>
        </div>
      </div>

      
    </div>
  );
}

export default Login;